/**
 * Swap Transaction Flow Hook
 * Handle approve → swap transaction sequence with silent airdrop
 */

import { BrowserProvider, Contract, parseUnits } from 'ethers';

const ERC20_ABI = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)'
];

const IDRA_SWAP_ABI = [
  'function swap(address paymentToken, uint256 paymentAmount, uint256 idraAmount, uint256 timestamp, uint256 expiry, bytes signature) external'
];

const BACKEND_URL = import.meta.env.VITE_SWAP_BACKEND_URL || 'http://localhost:8787';

// Token addresses from environment
const TOKEN_ADDRESSES_MAINNET = {
  USDC: import.meta.env.VITE_USDC_ADDRESS_MAINNET || '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
  USDT: import.meta.env.VITE_USDT_ADDRESS_MAINNET || '0xc2132D05D31c914a87C6611C10748AEb04B58e8F'
};

const TOKEN_ADDRESSES_TESTNET = {
  USDC: import.meta.env.VITE_USDC_ADDRESS_TESTNET || '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582', // Amoy testnet
  USDT: import.meta.env.VITE_USDT_ADDRESS_TESTNET || '0xA02f6adc7926efeBBd59Fd43A84f4E0c0c91e832'  // Amoy testnet
};

interface SwapParams {
  provider: BrowserProvider;
  walletAddress: string;
  token: string;
  amount: string;
  chainId: number;
  onProgress: (message: string) => void;
  onAirdrop: (txHash: string) => void;
}

export async function executeSwap({
  provider,
  walletAddress,
  token,
  amount,
  chainId,
  onProgress,
  onAirdrop
}: SwapParams) {
  const signer = await provider.getSigner();

  // Get contract addresses based on chain
  const isMainnet = chainId === 137;
  const idraSwapAddress = isMainnet
    ? import.meta.env.VITE_IDRA_SWAP_ADDRESS_MAINNET
    : import.meta.env.VITE_IDRA_SWAP_ADDRESS_TESTNET;

  // Token can be either a symbol (USDT) or an address (0x... for USDC)
  let paymentTokenAddress: string;

  if (token.startsWith('0x')) {
    // It's already an address (USDC)
    paymentTokenAddress = token;
  } else {
    // It's a symbol (USDT), look it up
    const tokenAddresses = isMainnet ? TOKEN_ADDRESSES_MAINNET : TOKEN_ADDRESSES_TESTNET;
    paymentTokenAddress = tokenAddresses[token as keyof typeof tokenAddresses];
  }

  if (!paymentTokenAddress || !idraSwapAddress) {
    throw new Error('Contract addresses not configured');
  }

  onProgress('Requesting swap signature...');

  // Step 1: Request EIP712 signature from backend
  const signResponse = await fetch(`${BACKEND_URL}/swap/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      paymentToken: paymentTokenAddress,
      paymentAmount: parseUnits(amount, 6).toString(), // USDC/USDT have 6 decimals
      walletAddress,
      chainId
    })
  });

  if (!signResponse.ok) {
    throw new Error('Failed to get swap signature');
  }

  const signData = await signResponse.json();
  const { signature, signatureTimestamp, expiry, idraAmount, airdropTx } = signData;

  // If airdrop was sent, notify user
  if (airdropTx) {
    onAirdrop(airdropTx);
  }

  // Step 2: Check allowance and approve if needed
  const paymentToken = new Contract(paymentTokenAddress, ERC20_ABI, signer);
  const currentAllowance = await paymentToken.allowance(walletAddress, idraSwapAddress);
  const paymentAmountWei = parseUnits(amount, 6);

  console.log('Allowance check:', {
    current: currentAllowance.toString(),
    required: paymentAmountWei.toString(),
    needsApproval: currentAllowance < paymentAmountWei
  });

  if (currentAllowance < paymentAmountWei) {
    // Display user-friendly token name (USDC for addresses, or the symbol itself)
    const tokenDisplay = token.startsWith('0x') ? 'USDC' : token;
    onProgress(`Approving ${tokenDisplay} spending...`);
    console.log('Sending approve transaction...');
    const approveTx = await paymentToken.approve(idraSwapAddress, paymentAmountWei);
    console.log('Approve tx sent:', approveTx.hash);
    onProgress('Waiting for approval confirmation...');
    const approveReceipt = await approveTx.wait();
    console.log('Approve tx confirmed:', approveReceipt);
    onProgress('Approval confirmed');
  }

  // Step 3: Execute swap
  onProgress('Executing swap...');
  const idraSwap = new Contract(idraSwapAddress, IDRA_SWAP_ABI, signer);

  // Use idraAmount from backend signature (already calculated with correct rate)
  const swapTx = await idraSwap.swap(
    paymentTokenAddress,
    paymentAmountWei,
    idraAmount,
    signatureTimestamp,
    expiry,
    signature
  );

  onProgress('Waiting for confirmation...');
  const receipt = await swapTx.wait();

  onProgress('Swap completed successfully!');
  return receipt;
}
